import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Fade,
  Paper,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HomeIcon from '@mui/icons-material/Home';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReplayIcon from '@mui/icons-material/Replay';

// ─── Mock Data ────────────────────────────────────────────────────────────────
const FLIGHTS = [
  { id: 'F1', flightNum: 'WN 1420', dest: 'LAX', depTime: '06:00' },
  { id: 'F2', flightNum: 'WN 2285', dest: 'ORD', depTime: '07:30' },
  { id: 'F3', flightNum: 'WN 3140', dest: 'BWI', depTime: '08:00' },
  { id: 'F4', flightNum: 'WN 4510', dest: 'DEN', depTime: '08:15' },
  { id: 'F5', flightNum: 'WN 1780', dest: 'PHX', depTime: '09:00' },
  { id: 'F6', flightNum: 'WN 2090', dest: 'BOS', depTime: '09:30' },
  { id: 'F7', flightNum: 'WN 3375', dest: 'ATL', depTime: '10:00' },
  { id: 'F8', flightNum: 'WN 4820', dest: 'BWI', depTime: '10:30' },
];

const NEAR_MISS_FLIGHTS = [
  FLIGHTS[2],
  { id: 'NM1', flightNum: 'WN 5920', dest: 'BWI', depTime: '11:30' },
];

const BELTS = [1, 2, 3, 4];

const buildLayoutFromSequence = (steps) => {
  const layout = { 1: [], 2: [], 3: [], 4: [] };

  steps.forEach((step) => {
    Object.keys(layout).forEach((belt) => {
      layout[belt] = layout[belt].filter((id) => id !== step.flightId);
    });
    layout[step.targetBelt].push(step.flightId);
  });

  return layout;
};

// Scene 4 candidate layouts — Belt 3 is closed, so only Belts 1, 2, 4 get flights
// F3=BWI 08:00, F8=BWI 10:30 — hard conflict example inside the 3-hour conflict window
const LAYOUT_A = { 1: ['F1','F3','F8'], 2: ['F2','F4','F5'], 3: [], 4: ['F6','F7'] };
const LAYOUT_B = { 1: ['F1'], 2: ['F2','F3','F4','F5','F8'], 3: [], 4: ['F6','F7'] };

const ASSIGNMENT_SEQUENCE = [
  {
    flightId: 'F1',
    targetBelt: 1,
    reason: 'Legal on B1, B2, and B4. B1 starts the first open stack.',
    eligibility: [
      { belt: 1, allowed: 1, note: 'Open and empty.' },
      { belt: 2, allowed: 1, note: 'Also legal.' },
      { belt: 3, allowed: 0, note: 'Closed all day.' },
      { belt: 4, allowed: 1, note: 'Open at 06:00.' },
    ],
  },
  {
    flightId: 'F3',
    targetBelt: 2,
    reason: 'B1, B2, and B4 are all legal. This example places the first BWI on Belt 2 so the later BWI has to stay away from that belt.',
    eligibility: [
      { belt: 1, allowed: 0, note: 'Already occupied while another open belt is still empty.' },
      { belt: 2, allowed: 1, note: 'Open and empty, so it claims the middle lane first.' },
      { belt: 3, allowed: 0, note: 'Closed all day.' },
      { belt: 4, allowed: 1, note: 'Still open and empty.' },
    ],
  },
  {
    flightId: 'F2',
    targetBelt: 4,
    reason: 'ORD is also legal on the open belts. This example sends it to Belt 4 to keep the three open belts seeded early.',
    eligibility: [
      { belt: 1, allowed: 0, note: 'Already occupied while Belt 4 is still empty.' },
      { belt: 2, allowed: 0, note: 'Already occupied while Belt 4 is still empty.' },
      { belt: 3, allowed: 0, note: 'Closed all day.' },
      { belt: 4, allowed: 1, note: 'Still open and gives the short-hours belt an early flight.' },
    ],
  },
  {
    flightId: 'F4',
    targetBelt: 1,
    reason: 'DEN is legal on B1, B2, and B4. In this teaching pass, the rotation comes back to Belt 1 first, so DEN lands there.',
    eligibility: [
      { belt: 1, allowed: 1, note: 'First legal belt in the current rotation.' },
      { belt: 2, allowed: 1, note: 'Also legal, but it comes later in the sequence.' },
      { belt: 3, allowed: 0, note: 'Closed all day.' },
      { belt: 4, allowed: 1, note: '08:15 is before 14:00, but Belt 4 is not first in this step order.' },
    ],
  },
  {
    flightId: 'F5',
    targetBelt: 2,
    reason: 'In this teaching pass, the next in-play slot after DEN on Belt 1 is Belt 2, so PHX stacks there.',
    eligibility: [
      { belt: 1, allowed: 0, note: 'Not in play for this step because Belt 1 just received the previous flight.' },
      { belt: 2, allowed: 1, note: 'Next open slot in the pattern, so PHX lands here.' },
      { belt: 3, allowed: 0, note: 'Closed all day.' },
      { belt: 4, allowed: 1, note: 'Still legal, but it comes later in the sequence.' },
    ],
  },
  {
    flightId: 'F6',
    targetBelt: 4,
    reason: 'After Belt 2 gets the PHX flight, Belt 4 becomes the next in-play open belt, so BOS drops there.',
    eligibility: [
      { belt: 1, allowed: 0, note: 'Recently assigned, so this teaching pass moves to the next open belt.' },
      { belt: 2, allowed: 0, note: 'Just used on the previous step, so this pass moves on.' },
      { belt: 3, allowed: 0, note: 'Closed all day.' },
      { belt: 4, allowed: 1, note: '09:30 is still inside hours, and it is the next open slot.' },
    ],
  },
  {
    flightId: 'F7',
    targetBelt: 1,
    reason: 'Once Belt 4 has been used, the teaching pattern rolls back to Belt 1. ATL is legal there, so it takes the first available slot.',
    eligibility: [
      { belt: 1, allowed: 1, note: 'First available legal belt in the sequence.' },
      { belt: 2, allowed: 0, note: 'Not in play yet on this rotation.' },
      { belt: 3, allowed: 0, note: 'Closed all day.' },
      { belt: 4, allowed: 1, note: 'Still legal, but Belt 1 comes first in the order.' },
    ],
  },
  {
    flightId: 'F8',
    targetBelt: 2,
    label: 'WN 4820 -> Belt 2 (conflict)',
    reason: 'If the second BWI goes onto Belt 2, both BWI flights end up too close together on the same belt. The conflict is visible immediately.',
    eligibility: [
      { belt: 1, allowed: 0, note: 'Not in play on this step.' },
      { belt: 2, allowed: 0, note: 'Rejected here because it creates the BWI conflict.' },
      { belt: 3, allowed: 0, note: 'Closed all day.' },
      { belt: 4, allowed: 1, note: 'Still available as the clean alternative.' },
    ],
    conflictFlights: ['F3', 'F8'],
  },
  {
    flightId: 'F8',
    targetBelt: 4,
    label: 'WN 4820 -> Belt 4 (resolved)',
    reason: 'The next belt in the pattern would be Belt 2, but that would create a BWI conflict with the 08:00 flight already there. Belt 4 is the next legal option.',
    eligibility: [
      { belt: 1, allowed: 0, note: 'Not in play on this step.' },
      { belt: 2, allowed: 0, note: 'Blocked here because it would put two BWI flights too close together.' },
      { belt: 3, allowed: 0, note: 'Closed all day.' },
      { belt: 4, allowed: 1, note: 'Next legal belt after Belt 2 is rejected for conflict.' },
    ],
    resolvedFlights: ['F8'],
  },
];

const ASSIGNMENT_STEP_BY_FLIGHT = ASSIGNMENT_SEQUENCE.reduce((acc, step, index) => {
  if (acc[step.flightId] === undefined) {
    acc[step.flightId] = index;
  }
  return acc;
}, {});

const LAYOUT_C = buildLayoutFromSequence(ASSIGNMENT_SEQUENCE);
const FINAL_LAYOUT = LAYOUT_C;

const getAssignmentLayout = (revealedSteps) => buildLayoutFromSequence(
  ASSIGNMENT_SEQUENCE.slice(0, revealedSteps)
);

const TOTAL_SCENES = 5;

// ─── Keyframe Styles (injected once on first mount) ──────────────────────────
const KEYFRAME_ID = 'algorithm-explainer-keyframes';
const KEYFRAME_CSS = `
  @keyframes ae-fadeSlideUp {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes ae-flashRed {
    0%, 100% { box-shadow: 0 0 0 0 rgba(255,82,82,0); }
    50%      { box-shadow: 0 0 12px 2px rgba(255,82,82,0.6); }
  }
  @keyframes ae-flashOrange {
    0%, 100% { box-shadow: 0 0 0 0 rgba(255,140,0,0); }
    50%      { box-shadow: 0 0 12px 2px rgba(255,140,0,0.5); }
  }
  @keyframes ae-pulse {
    0%, 100% { opacity: 1; }
    50%      { opacity: 0.5; }
  }
  @keyframes ae-checkPop {
    0%   { transform: scale(0); opacity: 0; }
    60%  { transform: scale(1.3); }
    100% { transform: scale(1); opacity: 1; }
  }
  @keyframes ae-goldGlow {
    0%, 100% { box-shadow: 0 0 8px rgba(249,182,18,0.3); }
    50%      { box-shadow: 0 0 20px rgba(249,182,18,0.7); }
  }
  @keyframes ae-bankFade {
    from { opacity: 1; transform: scale(1); }
    to   { opacity: 0.04; transform: scale(0.98); }
  }
`;

function injectKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(KEYFRAME_ID)) return;
  const style = document.createElement('style');
  style.id = KEYFRAME_ID;
  style.textContent = KEYFRAME_CSS;
  document.head.appendChild(style);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const flightById = (id) => FLIGHTS.find(f => f.id === id);

const glassCard = (extra = {}) => ({
  bgcolor: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: 2,
  p: 2,
  ...extra,
});

// ─── Shared Sub-Components ────────────────────────────────────────────────────

const SceneCaption = ({ children }) => (
  <Box sx={{ mt: 4, mx: 'auto', maxWidth: 700, textAlign: 'center' }}>
    <Typography
      variant="body1"
      sx={{ color: 'rgba(255,255,255,0.7)', fontStyle: 'italic', lineHeight: 1.6 }}
    >
      {children}
    </Typography>
  </Box>
);

const MiniFlightTile = ({ flight, delay = 0, highlight, highlightColor, dimmed, sx = {} }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      p: 1,
      bgcolor: dimmed ? 'rgba(255,255,255,0.02)' : 'rgba(255, 255, 255, 0.05)',
      borderRadius: 1,
      border: '1px solid',
      borderColor: highlight
        ? (highlightColor || '#FF5252')
        : 'rgba(255, 255, 255, 0.1)',
      opacity: dimmed ? 0.35 : 1,
      animation: delay > 0 ? `ae-fadeSlideUp 0.4s ease ${delay}ms both` : 'ae-fadeSlideUp 0.4s ease both',
      ...(highlight && highlightColor === '#FF5252' ? { animation: `ae-fadeSlideUp 0.4s ease ${delay}ms both, ae-flashRed 1.5s ease-in-out infinite` } : {}),
      ...(highlight && highlightColor === '#FF8C00' ? { animation: `ae-fadeSlideUp 0.4s ease ${delay}ms both, ae-flashOrange 1.5s ease-in-out infinite` } : {}),
      ...sx,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" sx={{ color: highlight ? (highlightColor || '#FF5252') : '#E2E2E2', fontWeight: 600, fontSize: '0.8rem' }}>
        {flight.dest}
      </Typography>
      <Typography variant="body2" sx={{ color: '#E2E2E2', fontWeight: 600, fontSize: '0.8rem' }}>
        {flight.flightNum}
      </Typography>
    </Box>
    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, fontSize: '0.75rem', flexShrink: 0 }}>
      {flight.depTime}
    </Typography>
  </Box>
);

const BuildColumn = ({ belt, ids, closed, highlightedFlightIds, resolvedFlightIds }) => (
  <Paper
    sx={{
      ...glassCard({ p: 1.25 }),
      animation: 'ae-fadeSlideUp 0.45s ease both',
      opacity: closed ? 0.45 : 1,
      bgcolor: closed ? 'rgba(255,82,82,0.05)' : 'rgba(255,255,255,0.05)',
      borderColor: closed ? 'rgba(255,82,82,0.25)' : 'rgba(255,255,255,0.1)',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, pb: 1, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
      <Typography variant="subtitle2" sx={{ color: closed ? '#FF5252' : '#F9B612', fontWeight: 700 }}>
        Belt {belt}
      </Typography>
      <Typography variant="caption" sx={{ color: closed ? '#FF5252' : 'rgba(255,255,255,0.5)', fontWeight: 700 }}>
        {closed ? '0' : ids.length}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minHeight: 120 }}>
      {ids
        .map((id) => (
          <MiniFlightTile
            key={id}
            flight={flightById(id)}
            highlight={highlightedFlightIds.includes(id) || resolvedFlightIds.includes(id)}
            highlightColor={
              highlightedFlightIds.includes(id)
                ? '#FF5252'
                : resolvedFlightIds.includes(id)
                  ? '#4caf50'
                  : undefined
            }
          />
        ))}
      {closed && (
        <Typography variant="caption" sx={{ color: 'rgba(255,82,82,0.45)', textAlign: 'center', py: 2 }}>
          closed
        </Typography>
      )}
    </Box>
  </Paper>
);

const BankFlightTile = ({ flight, stepIndex, revealedSteps }) => {
  const placed = stepIndex < revealedSteps;
  return (
    <Box
      sx={{
        opacity: placed ? 0.04 : 1,
        transform: placed ? 'scale(0.98)' : 'scale(1)',
        transition: 'opacity 0.24s ease, transform 0.24s ease',
      }}
    >
      <MiniFlightTile flight={flight} />
    </Box>
  );
};

const EligibilityChip = ({ belt, allowed, note }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      px: 0.75,
      py: 0.5,
      borderRadius: 999,
      bgcolor: allowed ? 'rgba(76,175,80,0.12)' : 'rgba(255,82,82,0.12)',
      border: '1px solid',
      borderColor: allowed ? 'rgba(76,175,80,0.25)' : 'rgba(255,82,82,0.25)',
    }}
  >
    <Typography variant="caption" sx={{ color: allowed ? '#4caf50' : '#FF8A80', fontWeight: 800, minWidth: 18 }}>
      B{belt}={allowed}
    </Typography>
    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.52)' }}>
      {note}
    </Typography>
  </Box>
);

const AssignmentDecisionRow = ({ step, index }) => {
  const flight = flightById(step.flightId);
  return (
    <Paper
      sx={{
        ...glassCard({ p: 1.25 }),
        animation: `ae-fadeSlideUp 0.35s ease ${index * 80}ms both`,
        borderColor: 'rgba(255,255,255,0.08)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2" sx={{ color: '#F9B612', fontWeight: 700 }}>
          Step {index + 1}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', fontWeight: 700 }}>
          {step.label || `${flight.flightNum} -> Belt ${step.targetBelt}`}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
        {step.eligibility.map((item) => (
          <EligibilityChip key={`${step.flightId}-${item.belt}`} {...item} />
        ))}
      </Box>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.62)', lineHeight: 1.5 }}>
        {step.reason}
      </Typography>
    </Paper>
  );
};

const AssignmentProgressPanel = ({ revealedSteps }) => {
  const completedSteps = ASSIGNMENT_SEQUENCE.slice(0, revealedSteps);
  const currentStep = ASSIGNMENT_SEQUENCE[revealedSteps] || null;

  return (
        <Paper sx={{ ...glassCard({ p: 1.5 }), borderColor: 'rgba(255,255,255,0.12)' }}>
          <Typography variant="subtitle2" sx={{ color: '#F9B612', fontWeight: 700, mb: 1 }}>
            Why each flight can or cannot go there
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.58)', display: 'block', mb: 1.5 }}>
            In this teaching pass, `1` means the belt is still in play for the current placement. `0` means it is blocked either by a hard rule or because the example is still filling open empty belts first. Use left/right to place flights one by one.
          </Typography>

      {currentStep ? (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 0.75 }}>
            Next placement
          </Typography>
          <AssignmentDecisionRow step={currentStep} index={revealedSteps} />
        </Box>
      ) : (
        <Paper sx={{ ...glassCard({ p: 1.25 }), borderColor: 'rgba(76,175,80,0.2)', mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ color: '#4caf50', fontWeight: 700, mb: 0.5 }}>
            All 8 flights placed
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.62)' }}>
            The teaching layout is fully built. Move to the next phase to compare layouts and scores.
          </Typography>
        </Paper>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: completedSteps.length > 0 ? 1 : 0 }}>
        {completedSteps.map((step, index) => {
          const flight = flightById(step.flightId);
          return (
            <Chip
              key={`${step.flightId}-${index}`}
              label={`${index + 1}. ${step.label || `${flight.dest} -> B${step.targetBelt}`}`}
              size="small"
              sx={{
                bgcolor: step.conflictFlights ? 'rgba(255,82,82,0.12)' : 'rgba(76,175,80,0.12)',
                color: step.conflictFlights ? '#FFB3B3' : '#B8F5C0',
                border: step.conflictFlights ? '1px solid rgba(255,82,82,0.25)' : '1px solid rgba(76,175,80,0.2)',
                fontWeight: 700,
              }}
            />
          );
        })}
      </Box>
      {completedSteps.length > 0 && (
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.48)' }}>
          Completed placements: {completedSteps.length} of {ASSIGNMENT_SEQUENCE.length}
        </Typography>
      )}
    </Paper>
  );
};

// ─── Scene 1: Import Flights ──────────────────────────────────────────────────

const Scene1 = () => (
  <Box>
    <Typography variant="h5" sx={{ color: '#F9B612', fontWeight: 700, mb: 3, textAlign: 'center' }}>
      Import Flights
    </Typography>

    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
      gap: 1.5,
      maxWidth: 560,
      mx: 'auto',
    }}>
      {FLIGHTS.map((f, i) => (
        <MiniFlightTile key={f.id} flight={f} delay={i * 100} />
      ))}
    </Box>

    <SceneCaption>
      Flights are imported from the gate sheet and sorted by departure time.
    </SceneCaption>
  </Box>
);

// ─── Shared Settings Panel ────────────────────────────────────────────────────

const HardRuleCard = ({ icon, title, children, delay }) => (
  <Paper
    sx={{
      ...glassCard(),
      borderColor: 'rgba(255, 82, 82, 0.4)',
      animation: `ae-fadeSlideUp 0.5s ease ${delay}ms both`,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      <Box sx={{ color: '#FF5252' }}>{icon}</Box>
      <Typography variant="subtitle1" sx={{ color: '#FF5252', fontWeight: 700 }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Paper>
);

const ExampleSettingsPanel = () => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
      gap: 2,
      mb: 3,
    }}
  >
    <HardRuleCard icon={<BlockIcon />} title="Example Hard Rules (1 or 0)" delay={0}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Chip label="Belt 3 closed all day" size="small" sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(255,82,82,0.14)', color: '#FF8A80', fontWeight: 700 }} />
        <Chip label="Belt 4 open 06:00–14:00" size="small" sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(249,182,18,0.14)', color: '#F9B612', fontWeight: 700 }} />
        <Chip label="Max 3 flights per belt per hour" size="small" sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(255,255,255,0.08)', color: '#E2E2E2', fontWeight: 700 }} />
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>
          These settings decide whether a belt gets a hard `1` or `0` for a flight.
        </Typography>
      </Box>
    </HardRuleCard>

    <Paper
      sx={{
        ...glassCard(),
        borderColor: 'rgba(255,165,0,0.2)',
        animation: 'ae-fadeSlideUp 0.5s ease 150ms both',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <WarningAmberIcon sx={{ color: '#F9B612' }} />
        <Typography variant="subtitle1" sx={{ color: '#F9B612', fontWeight: 700 }}>
          Example Scoring Preferences
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Chip label="Conflict pressure window: 3 hours" size="small" sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(255,82,82,0.12)', color: '#FFB3A7', fontWeight: 700 }} />
        <Chip label="Near-miss window: +1 hour" size="small" sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(255,140,0,0.12)', color: '#FFD08A', fontWeight: 700 }} />
        <Chip label="Balance matters after conflict pressure" size="small" sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(76,175,80,0.12)', color: '#9BE7A1', fontWeight: 700 }} />
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>
          These settings stay active through every phase below. First the solver checks legal `1/0` belts, then it scores the eligible choices.
        </Typography>
      </Box>
    </Paper>
  </Box>
);

// ─── Scene 2: Build Candidate Layout ──────────────────────────────────────────

const Scene2 = ({ revealedSteps }) => (
  <Box>
    <Typography variant="h5" sx={{ color: '#F9B612', fontWeight: 700, mb: 3, textAlign: 'center' }}>
      Assign Flights Into Belts
    </Typography>

    <Box sx={{ maxWidth: 1080, mx: 'auto' }}>
      {(() => {
        const currentLayout = getAssignmentLayout(revealedSteps);
        const lastCompletedStep = revealedSteps > 0 ? ASSIGNMENT_SEQUENCE[revealedSteps - 1] : null;
        const highlightedFlightIds = lastCompletedStep?.conflictFlights || [];
        const resolvedFlightIds = lastCompletedStep?.resolvedFlights || [];

        return (
          <>
      <Paper sx={{ ...glassCard({ p: 1.5 }), borderColor: 'rgba(255,255,255,0.12)', mb: 2.5 }}>
        <Typography variant="subtitle2" sx={{ color: '#F9B612', fontWeight: 700, mb: 1 }}>
          Flight bank
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.58)', display: 'block', mb: 1.5 }}>
          These eight flights start at the top. As the example runs, each one drops into its chosen belt and fades in the bank above.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
          {FLIGHTS.map((flight) => (
            <BankFlightTile
              key={flight.id}
              flight={flight}
              stepIndex={ASSIGNMENT_STEP_BY_FLIGHT[flight.id] ?? 0}
              revealedSteps={revealedSteps}
            />
          ))}
        </Box>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.2fr 0.8fr' }, gap: 2 }}>
        <Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1.5 }}>
            {BELTS.map((belt) => (
              <BuildColumn
                key={belt}
                belt={belt}
                ids={currentLayout[belt] || []}
                closed={belt === 3}
                highlightedFlightIds={highlightedFlightIds}
                resolvedFlightIds={resolvedFlightIds}
              />
            ))}
          </Box>
        </Box>

        <AssignmentProgressPanel revealedSteps={revealedSteps} />
      </Box>
          </>
        );
      })()}
    </Box>

    <SceneCaption>
      This is a teaching view of one candidate layout forming flight by flight. The real solver still compares many legal layouts globally,
      but this scene shows how `1/0` eligibility and stacking work in a way people can follow.
    </SceneCaption>
  </Box>
);

// ─── Scene 3: Soft Preferences ────────────────────────────────────────────────

const PriorityBar = ({ level }) => {
  // level: 4 = highest, 1 = lowest
  const heights = { 4: '100%', 3: '75%', 2: '50%', 1: '30%' };
  const colors = { 4: '#FF5252', 3: '#FF8C00', 2: '#F9B612', 1: '#4caf50' };
  return (
    <Box sx={{ width: 6, height: 40, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 1, position: 'relative', flexShrink: 0 }}>
      <Box sx={{
        position: 'absolute', bottom: 0, width: '100%',
        height: heights[level], bgcolor: colors[level],
        borderRadius: 1, transition: 'height 0.6s ease',
      }} />
    </Box>
  );
};

const SoftPrefCard = ({ icon, title, priority, children, delay }) => (
  <Paper
    sx={{
      ...glassCard(),
      borderColor: 'rgba(255,165,0,0.2)',
      animation: `ae-fadeSlideUp 0.5s ease ${delay}ms both`,
      display: 'flex',
      gap: 2,
    }}
  >
    <PriorityBar level={priority} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box sx={{ color: '#F9B612' }}>{icon}</Box>
        <Typography variant="subtitle2" sx={{ color: '#F9B612', fontWeight: 700 }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  </Paper>
);

const MiniBalanceChart = ({ bars, alertColor, neutralColor, closedColor }) => {
  const maxValue = Math.max(...bars.map((bar) => bar.value), 1);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${bars.length}, minmax(0, 1fr))`,
        gap: 1,
        alignItems: 'end',
        height: 86,
        mb: 1,
      }}
    >
      {bars.map((bar) => {
        const height = bar.closed ? 6 : Math.max(12, (bar.value / maxValue) * 56);
        const isAlert = !bar.closed && bar.value === maxValue && bar.value > 0;
        return (
          <Box key={bar.label} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: bar.closed ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.58)',
                fontSize: '0.62rem',
                mb: 0.5,
                fontWeight: 700,
              }}
            >
              {bar.closed ? 'closed' : `${bar.value} flights`}
            </Typography>
            <Box
              sx={{
                width: '100%',
                maxWidth: 54,
                height,
                bgcolor: bar.closed
                  ? 'rgba(255,255,255,0.04)'
                  : isAlert
                    ? `${alertColor}22`
                    : `${neutralColor}24`,
                border: '1px solid',
                borderColor: bar.closed
                  ? closedColor
                  : isAlert
                    ? alertColor
                    : neutralColor,
                borderRadius: '4px 4px 0 0',
                transition: 'height 0.6s ease',
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: bar.closed ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.45)',
                fontSize: '0.66rem',
                mt: 0.4,
                fontWeight: 700,
              }}
            >
              {bar.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const Scene3 = () => (
  <Box>
    <Typography variant="h5" sx={{ color: '#F9B612', fontWeight: 700, mb: 3, textAlign: 'center' }}>
      Score The Eligible Choices
    </Typography>

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 620, mx: 'auto' }}>
      {/* Destination Conflicts */}
      <SoftPrefCard icon={<WarningAmberIcon />} title="Destination Conflicts" priority={4} delay={0}>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <MiniFlightTile flight={FLIGHTS[2]} highlight highlightColor="#FF5252" sx={{ flex: 1, animation: 'ae-flashRed 1.5s ease-in-out infinite' }} />
          <MiniFlightTile flight={FLIGHTS[7]} highlight highlightColor="#FF5252" sx={{ flex: 1, animation: 'ae-flashRed 1.5s ease-in-out infinite' }} />
        </Box>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
          Same destination within the conflict window on the same belt or paired belt.
        </Typography>
      </SoftPrefCard>

      {/* Near-Miss Conflicts */}
      <SoftPrefCard icon={<WarningAmberIcon />} title="Near-Miss Conflicts" priority={3} delay={150}>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <MiniFlightTile flight={NEAR_MISS_FLIGHTS[0]} highlight highlightColor="#FF8C00" sx={{ flex: 1, opacity: 0.7 }} />
          <MiniFlightTile flight={NEAR_MISS_FLIGHTS[1]} highlight highlightColor="#FF8C00" sx={{ flex: 1, opacity: 0.7 }} />
        </Box>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
          Flights just outside the 3-hour window still create pressure.
        </Typography>
      </SoftPrefCard>

      {/* Hourly Balance */}
      <SoftPrefCard icon={<WarningAmberIcon />} title="Hourly Balance" priority={2} delay={300}>
        <MiniBalanceChart
          bars={[
            { label: 'B1', value: 4 },
            { label: 'B2', value: 1 },
            { label: 'B3', value: 0, closed: true },
            { label: 'B4', value: 3 },
          ]}
          alertColor="#FF8C00"
          neutralColor="#F9B612"
          closedColor="rgba(255,255,255,0.16)"
        />
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
          Spread flights evenly within each hour across the belts that are actually available.
        </Typography>
      </SoftPrefCard>

      {/* Overall Load Balance */}
      <SoftPrefCard icon={<WarningAmberIcon />} title="Overall Load Balance" priority={1} delay={450}>
        <MiniBalanceChart
          bars={[
            { label: 'B1', value: 4 },
            { label: 'B2', value: 2 },
            { label: 'B3', value: 0, closed: true },
            { label: 'B4', value: 2 },
          ]}
          alertColor="#FF8C00"
          neutralColor="#4caf50"
          closedColor="rgba(255,255,255,0.16)"
        />
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
          Keep the same 8-flight workload roughly even across available belts, but only after higher-priority conflict rules are satisfied.
        </Typography>
      </SoftPrefCard>
    </Box>

    <SceneCaption>
      These are preferences, not absolutes. The solver weighs them by priority — avoiding a destination conflict
      matters far more than perfect balance. Sometimes a slightly uneven belt is the best option if it avoids worse conflicts.
    </SceneCaption>
  </Box>
);

// ─── Scene 4: Solver Evaluates Layouts ────────────────────────────────────────

const LayoutGrid = ({ label, layout, issues, borderColor, checkmark, delay }) => {
  return (
    <Paper
      sx={{
        ...glassCard({ p: 1.5 }),
        flex: 1,
        minWidth: 0,
        borderColor: borderColor || 'rgba(255,255,255,0.1)',
        animation: `ae-fadeSlideUp 0.5s ease ${delay}ms both${checkmark ? ', ae-goldGlow 2s ease-in-out infinite' : ''}`,
        position: 'relative',
      }}
    >
      <Typography variant="subtitle2" sx={{ color: '#E2E2E2', fontWeight: 700, mb: 1, textAlign: 'center' }}>
        {label}
      </Typography>

      {/* Mini grid */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {BELTS.map(belt => {
          const ids = layout[belt] || [];
          return (
            <Box key={belt} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', width: 24, flexShrink: 0, fontSize: '0.65rem' }}>
                B{belt}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.25, flex: 1, flexWrap: 'wrap' }}>
                {ids.map(id => {
                  const f = flightById(id);
                  const isConflict = issues?.conflicts?.includes(id);
                  const isOnImbalancedBelt = issues?.imbalance?.includes(belt);
                  const isNearMiss = issues?.nearMiss?.includes(id);
                  return (
                    <Chip
                      key={id}
                      label={f.dest}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        bgcolor: isConflict ? 'rgba(255,82,82,0.2)' : isNearMiss ? 'rgba(255,140,0,0.15)' : isOnImbalancedBelt ? 'rgba(255,140,0,0.1)' : 'rgba(255,255,255,0.08)',
                        color: isConflict ? '#FF5252' : isNearMiss ? '#FF8C00' : isOnImbalancedBelt ? '#FF8C00' : '#E2E2E2',
                        border: '1px solid',
                        borderColor: isConflict ? 'rgba(255,82,82,0.5)' : isNearMiss ? 'rgba(255,140,0,0.4)' : isOnImbalancedBelt ? 'rgba(255,140,0,0.3)' : 'rgba(255,255,255,0.15)',
                        animation: isConflict ? 'ae-flashRed 1.5s ease-in-out infinite' : 'none',
                        '& .MuiChip-label': { px: 0.5 },
                      }}
                    />
                  );
                })}
                {ids.length === 0 && (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.6rem', fontStyle: 'italic' }}>
                    —
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Issue summary */}
      <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        {issues?.penaltyLabel && (
          <Typography variant="caption" sx={{ color: issues.penaltyColor || 'rgba(255,255,255,0.5)', fontSize: '0.65rem', display: 'block' }}>
            {issues.penaltyLabel}
          </Typography>
        )}
      </Box>

      {/* Gold checkmark */}
      {checkmark && (
        <Box sx={{
          position: 'absolute', top: -10, right: -10,
          animation: 'ae-checkPop 0.5s ease 800ms both',
        }}>
          <CheckCircleIcon sx={{ color: '#F9B612', fontSize: 28, filter: 'drop-shadow(0 0 6px rgba(249,182,18,0.6))' }} />
        </Box>
      )}
    </Paper>
  );
};

const Scene4 = () => (
  <Box>
    <Typography variant="h5" sx={{ color: '#F9B612', fontWeight: 700, mb: 3, textAlign: 'center' }}>
      The Solver Optimizes All At Once
    </Typography>

    <Box sx={{ maxWidth: 820, mx: 'auto', mb: 3 }}>
      <Paper sx={{ ...glassCard({ p: 2 }), borderColor: 'rgba(249,182,18,0.2)', mb: 2.5 }}>
        <Typography variant="subtitle2" sx={{ color: '#F9B612', fontWeight: 700, mb: 1.5 }}>
          How the constraint solver works
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, mb: 2 }}>
          The solver creates a yes/no variable for every possible flight-to-belt pairing — all at the same time.
          It then solves a single math problem that considers every assignment simultaneously, rather than
          building layouts one by one and comparing them.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Chip label="Conflict avoidance — weight 1000" size="small" sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(255,82,82,0.14)', color: '#FF8A80', fontWeight: 700 }} />
          <Chip label="Near-miss avoidance — weight 300" size="small" sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(255,140,0,0.12)', color: '#FFD08A', fontWeight: 700 }} />
          <Chip label="Hourly balance — weight 200" size="small" sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(249,182,18,0.14)', color: '#F9B612', fontWeight: 700 }} />
          <Chip label="Overall load balance — weight 50" size="small" sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(76,175,80,0.12)', color: '#9BE7A1', fontWeight: 700 }} />
          <Chip label="Round-robin preference — weight 10" size="small" sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(255,255,255,0.08)', color: '#E2E2E2', fontWeight: 700 }} />
        </Box>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mt: 1.5, lineHeight: 1.5 }}>
          The solver minimizes the total weighted penalty across all flights at once. A single destination
          conflict costs 1000 points, so the solver will accept some imbalance (50 points each) if it means
          avoiding a conflict.
        </Typography>
      </Paper>

      <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 700, mb: 1.5, textAlign: 'center' }}>
        To see why this matters, compare what different assignments would score:
      </Typography>

      <Box sx={{
        display: 'flex',
        gap: 2,
        flexDirection: { xs: 'column', md: 'row' },
      }}>
        <LayoutGrid
          label="If it placed BWI together"
          layout={LAYOUT_A}
          issues={{
            conflicts: ['F3', 'F8'],
            penaltyLabel: 'BWI conflict on Belt 1 — penalty 1000+',
            penaltyColor: '#FF5252',
          }}
          borderColor="rgba(255,82,82,0.3)"
          delay={0}
        />
        <LayoutGrid
          label="If it overloaded one belt"
          layout={LAYOUT_B}
          issues={{
            imbalance: [2],
            penaltyLabel: 'No conflicts, but Belt 2 overloaded — penalty ~200',
            penaltyColor: '#FF8C00',
          }}
          borderColor="rgba(255,140,0,0.3)"
          delay={200}
        />
        <LayoutGrid
          label="What the solver finds"
          layout={LAYOUT_C}
          issues={{
            penaltyLabel: 'No conflicts, balanced loads — lowest total penalty',
            penaltyColor: '#4caf50',
          }}
          borderColor="rgba(76,175,80,0.4)"
          checkmark
          delay={400}
        />
      </Box>
    </Box>

    <SceneCaption>
      The solver doesn't build these layouts one at a time and compare them. It solves for the
      best assignment of all flights simultaneously — these examples just show why the math
      steers toward the result it finds.
    </SceneCaption>
  </Box>
);

// ─── Scene 5: The Result ──────────────────────────────────────────────────────

const Annotation = ({ children, delay }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1,
      animation: `ae-fadeSlideUp 0.4s ease ${delay}ms both`,
    }}
  >
    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 16, mt: 0.3, flexShrink: 0 }} />
    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
      {children}
    </Typography>
  </Box>
);

const Scene5 = () => (
  <Box>
    <Typography variant="h5" sx={{ color: '#F9B612', fontWeight: 700, mb: 3, textAlign: 'center' }}>
      The Result
    </Typography>

    {/* Belt column view matching the app style */}
    <Box sx={{
      display: 'flex',
      gap: 2,
      maxWidth: 760,
      mx: 'auto',
      mb: 3,
      flexDirection: { xs: 'column', sm: 'row' },
    }}>
      {BELTS.map((belt, bi) => {
        const ids = FINAL_LAYOUT[belt] || [];
        const isClosed = belt === 3;
        return (
          <Paper
            key={belt}
            sx={{
              ...glassCard({ p: 1.5 }),
              flex: 1,
              minWidth: 0,
              animation: `ae-fadeSlideUp 0.5s ease ${bi * 120}ms both`,
              opacity: isClosed ? 0.45 : 1,
              bgcolor: isClosed ? 'rgba(255,82,82,0.05)' : 'rgba(255, 255, 255, 0.05)',
              borderColor: isClosed ? 'rgba(255,82,82,0.25)' : 'rgba(255, 255, 255, 0.1)',
            }}
          >
            {/* Belt header */}
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              mb: 1, pb: 1, borderBottom: '1px solid rgba(255,255,255,0.1)',
            }}>
              <Typography variant="subtitle2" sx={{ color: isClosed ? '#FF5252' : '#F9B612', fontWeight: 700 }}>
                Belt {belt}
              </Typography>
              <Chip
                label={isClosed ? 'CLOSED' : `${ids.length} flights`}
                size="small"
                sx={{
                  height: 18, fontSize: '0.6rem', fontWeight: 600,
                  bgcolor: isClosed ? 'rgba(255,82,82,0.15)' : 'rgba(255,255,255,0.08)',
                  color: isClosed ? '#FF5252' : 'rgba(255,255,255,0.6)',
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            </Box>

            {/* Flights */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {ids.map((id, fi) => {
                const f = flightById(id);
                return <MiniFlightTile key={id} flight={f} delay={bi * 120 + fi * 80} />;
              })}
              {ids.length === 0 && (
                <Typography variant="caption" sx={{ color: isClosed ? 'rgba(255,82,82,0.4)' : 'rgba(255,255,255,0.3)', textAlign: 'center', py: 2 }}>
                  {isClosed ? 'Closed — no flights assigned' : 'No flights assigned'}
                </Typography>
              )}
            </Box>
          </Paper>
        );
      })}
    </Box>

    {/* Annotations */}
    <Box sx={{ maxWidth: 600, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Annotation delay={500}>
        BWI flights separated — the 08:00 BWI claims Belt 2 early, so the 10:30 BWI is pushed to Belt 4 (destination conflict avoided)
      </Annotation>
      <Annotation delay={700}>
        Belt 3 is closed — zero flights assigned, exactly as the hard rule requires
      </Annotation>
      <Annotation delay={900}>
        Belt 4 stays lighter than Belts 1 and 2 because it closes earlier, so the later legal flights stack onto the full-day belts instead
      </Annotation>
    </Box>

    <SceneCaption>
      The final layout respects all hard rules and minimizes soft penalties.
      Some belts may have slightly different loads — that's intentional when it avoids worse problems.
    </SceneCaption>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const SCENES = [Scene1, Scene2, Scene3, Scene4, Scene5];
const SCENE_TITLES = [
  'Import Flights',
  'Build A Candidate',
  'Score The Choices',
  'Solver Weights',
  'The Result',
];

const TRANSITION_MS = 250;

const AlgorithmExplainer = () => {
  const [currentScene, setCurrentScene] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const [revealedAssignmentSteps, setRevealedAssignmentSteps] = useState(0);
  const transitionTimerRef = useRef(null);

  // Inject CSS keyframes on first mount
  useEffect(() => { injectKeyframes(); }, []);

  const clearTimers = useCallback(() => {
    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }
  }, []);

  const goToScene = useCallback((index) => {
    if (index < 0 || index >= TOTAL_SCENES || index === currentScene) return;
    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current);
    }
    setFadeIn(false);
    transitionTimerRef.current = setTimeout(() => {
      setCurrentScene(index);
      setRevealedAssignmentSteps(0);
      setFadeIn(true);
      transitionTimerRef.current = null;
    }, TRANSITION_MS);
  }, [currentScene]);

  const handleNextAction = useCallback(() => {
    if (currentScene === 1) {
      if (revealedAssignmentSteps < ASSIGNMENT_SEQUENCE.length) {
        setRevealedAssignmentSteps((prev) => prev + 1);
        return;
      }
    }
    goToScene(currentScene + 1);
  }, [currentScene, goToScene, revealedAssignmentSteps]);

  const handlePrevAction = useCallback(() => {
    if (currentScene === 1 && revealedAssignmentSteps > 0) {
      setRevealedAssignmentSteps((prev) => prev - 1);
      return;
    }
    goToScene(currentScene - 1);
  }, [currentScene, goToScene, revealedAssignmentSteps]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNextAction();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevAction();
      } else if (e.key.toLowerCase() === 'r') {
        e.preventDefault();
        clearTimers();
        setFadeIn(true);
        setCurrentScene(0);
        setRevealedAssignmentSteps(0);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [clearTimers, handleNextAction, handlePrevAction]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const handleReplay = () => {
    clearTimers();
    setFadeIn(true);
    setCurrentScene(0);
    setRevealedAssignmentSteps(0);
  };

  const ActiveScene = SCENES[currentScene];
  const progressPercent = ((currentScene + 1) / TOTAL_SCENES) * 100;

  return (
    <Box sx={{
      minHeight: '100vh',
      px: { xs: 2, sm: 4 },
      py: 4,
      maxWidth: 960,
      mx: 'auto',
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <IconButton
          component={Link}
          to="/"
          sx={{
            color: 'rgba(255,255,255,0.6)',
            '&:hover': { color: '#F9B612' },
          }}
        >
          <HomeIcon />
        </IconButton>
        <Typography variant="h6" sx={{ color: '#E2E2E2', fontWeight: 600 }}>
          How the Belt Optimizer Works
        </Typography>
        <Box sx={{ width: 40 }} />
      </Box>

      <Paper sx={{ ...glassCard({ p: { xs: 2, sm: 3 } }), overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" sx={{ color: '#F9B612', letterSpacing: '0.14em' }}>
              Interactive Walkthrough
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              Step through the example manually. Use the arrows, phase chips, or keyboard arrows. Press `R` to restart.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <IconButton
              onClick={handlePrevAction}
              disabled={currentScene === 0 && revealedAssignmentSteps === 0}
              sx={{
                color: currentScene === 0 && revealedAssignmentSteps === 0 ? 'rgba(255,255,255,0.25)' : '#F9B612',
                '&:hover': { bgcolor: 'rgba(249,182,18,0.1)' },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <IconButton
              onClick={handleReplay}
              sx={{ color: '#F9B612', '&:hover': { bgcolor: 'rgba(249,182,18,0.1)' } }}
            >
              <ReplayIcon />
            </IconButton>
            <IconButton
              onClick={handleNextAction}
              disabled={currentScene === TOTAL_SCENES - 1}
              sx={{
                color: currentScene === TOTAL_SCENES - 1 ? 'rgba(255,255,255,0.25)' : '#F9B612',
                '&:hover': { bgcolor: 'rgba(249,182,18,0.1)' },
              }}
            >
              <ArrowForwardIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              Phase {currentScene + 1} of {TOTAL_SCENES}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              {SCENE_TITLES[currentScene]}
            </Typography>
          </Box>
          <Box sx={{ height: 8, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: `${progressPercent}%`,
                borderRadius: 999,
                bgcolor: '#F9B612',
                transition: 'width 0.4s ease',
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          {SCENE_TITLES.map((title, i) => (
            <Chip
              key={title}
              label={`${i + 1}. ${title}`}
              onClick={() => {
                clearTimers();
                goToScene(i);
              }}
              size="small"
              sx={{
                bgcolor: i === currentScene
                  ? 'rgba(249,182,18,0.2)'
                  : i < currentScene
                    ? 'rgba(76,175,80,0.14)'
                    : 'rgba(255,255,255,0.06)',
                color: i === currentScene ? '#F9B612' : 'rgba(255,255,255,0.65)',
                border: '1px solid',
                borderColor: i === currentScene
                  ? 'rgba(249,182,18,0.45)'
                  : 'rgba(255,255,255,0.08)',
                fontWeight: 600,
              }}
            />
          ))}
        </Box>

        <ExampleSettingsPanel />

        <Fade in={fadeIn} timeout={300}>
          <Box sx={{ minHeight: 430 }}>
            {currentScene === 1 ? (
              <Scene2 revealedSteps={revealedAssignmentSteps} />
            ) : (
              <ActiveScene />
            )}
          </Box>
        </Fade>
      </Paper>

      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Typography
          variant="caption"
          sx={{ display: 'block', color: 'rgba(255,255,255,0.4)' }}
        >
          {currentScene === 1
            ? `Use left/right to place flights one at a time. ${revealedAssignmentSteps} of ${ASSIGNMENT_SEQUENCE.length} placed.`
            : 'Use `Previous` and `Next` to walk through the example at your own pace.'}
        </Typography>
      </Box>

      {/* Back to Home link */}
      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Typography
          component={Link}
          to="/"
          variant="body2"
          sx={{
            color: 'rgba(255,255,255,0.5)',
            textDecoration: 'none',
            '&:hover': { color: '#F9B612', textDecoration: 'underline' },
          }}
        >
          Back to Home
        </Typography>
      </Box>
    </Box>
  );
};

export default AlgorithmExplainer;
